<template>
  <div>
    <!-- rating modal -->
    <Modal :isOpen="isModalOpen" :changeModalState="changeModalState" :disableScrolling="true"
      :modalTitle="isEditMode ? $t('general.edit_rating') : $t('user.rate_store_modal_title')" centeredTitle smallTitle size="4xl" :hideHeadersBorder="true">
      <div
        class="relative pb-6 md:pb-7 px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12 3xl:px-13 overscroll-none overflow-y-auto min-h-[calc(15vh)] max-h-[80vh]">
        <!-- shows when loading -->
        <div v-if="isLoading || isUpdateDataStore" class="section-loader bg-white z-[1]">
          <div class="lds-dual-ring"></div>
        </div>
        <div v-else class="relative">
          <div class="sticky top-[-3px] z-[1] bg-white flex md:items-center justify-start md:justify-center gap-3"
            v-if="ratingData">
            <span class="text-[#848484] text-[17px] md:text-[18px] lg:text-[20px]">({{
      arabizeNumbers(
        Number(ratingData.ratings_count).toLocaleString()
      )
    }})</span>

            <div class="inline-flex items-center gap-2 py-1rounded-3xl text-[18px] sm:text-[20px] md:text-[21px]">
              <strong v-if="!isRTL" class="font-bold text-black text-[21px] md:text-[24px] lg:text-[25px]">{{
      ratingData.ratings_avg
        ? arabizeNumbers(
          Number(
            roundWithHalf(ratingData.ratings_avg)
          ).toLocaleString()
        )
        : arabizeNumbers(Number(0).toLocaleString())
    }}/{{ arabizeNumbers(Number(5).toLocaleString()) }}</strong>
              <strong v-else class="font-bold text-black text-[21px] md:text-[24px] lg:text-[25px]">{{
      arabizeNumbers(Number(5).toLocaleString()) }}/{{
      ratingData.ratings_avg
        ? arabizeNumbers(
          Number(
            roundWithHalf(ratingData.ratings_avg)
          ).toLocaleString()
        )
        : arabizeNumbers(Number(0).toLocaleString())
    }}</strong>

              <img :src="starIcon" alt="starIcon"
                class="w-[22px] h-[22px] sm:w-[23px] sm:h-[23px] md:w-[28px] md:h-[28px] lg:w-[31px] lg:h-[31px]"
                width="31" height="31" />
            </div>
          </div>
          <!-- shows if already gave feedback-->
          <div v-else-if="hasGivenFeedback" class="py-4">
            <strong>{{ $t("user.already_rated") }}</strong>
          </div>
          <!-- shows if they didn't -->
          <!-- rate form -->
          <form ref="provideFeedbackFormRef" :class="`${showFormShadows ? 'shadow-md' : ''
      } flex flex-col py-2 p-0 mt-[-5px]`.trim()
      " @submit.prevent="() => addRating()">
            <!-- rate -->
            <div class="flex justify-center md:block mt-1.5 md:mt-0">
              <div class="flex items-center gap-3">
                <SelectableStarsRating @changeRating="(newVal) => (rateForm.selectedRating = newVal)"
                  :isDisabled="isLoading" :initialRating="rateForm.selectedRating" />
                <strong v-if="!isRTL" class="font-bold text-black text-[21px] md:text-[24px] lg:text-[25px]">{{
      rateForm.selectedRating
        ? arabizeNumbers(
          Number(
            roundWithHalf(rateForm.selectedRating)
          ).toLocaleString()
        )
        : arabizeNumbers(Number(0).toLocaleString())
    }}/{{ arabizeNumbers(Number(5).toLocaleString()) }}</strong>
                <strong v-else class="font-bold text-black text-[21px] md:text-[24px] lg:text-[25px]">{{
      arabizeNumbers(Number(5).toLocaleString()) }}/{{
      rateForm.selectedRating
        ? arabizeNumbers(
          Number(
            roundWithHalf(rateForm.selectedRating)
          ).toLocaleString()
        )
        : arabizeNumbers(Number(0).toLocaleString())
    }}</strong>
              </div>
            </div>
            <div v-if="isRatingFormSubmitted &&
      rateForm &&
      rateForm.selectedRating <= 0
      " class="text-red-800 mt-3">
              {{ $t("general.rating_is_required") }}
            </div>
            <textarea :disabled="isLoading" v-model="rateForm.comment"
              class="border border-[#D8D8D8] placeholder:text-[#C8C8C8] rounded-[7px] duration-200 mt-6 min-h-[75px] md:min-h-[90px] max-h-[150px] w-full"
              :placeholder="$t('user.rating_placeholder')" />
            <!-- submit form button -->
            <div class="flex justify-center mt-0 md:mt-1 lg:mt-2">
              <input :class="`${isLoading ? 'disabled' : ''
        } max-w-[310px] md:max-w-[320px] 3xl:max-w-[326px] w-full bg-[#0963FF] hover:bg-[#0963FF]/90 transition-colors duration-150 cursor-pointer text-white mt-3 font-bold py-[0.525rem] px-6 sm:px-10 md:px-12 text-[15px] sm:text-[16px] md:text-[17px] lg:text-[18px] rounded-[7px]`.trim()
      " type="submit" :value="isLoading ? $t('general.loading') : (isEditMode ? $t('general.edit_rating') : $t('general.rating'))
      " />
            </div>
          </form>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
// Icons
import StarRating from "@/components/generic/StarRating.vue";
import Star from "@/assets/shared/icons/Star.vue";
import {
  roundWithHalf,
  arabizeNumbers,
  MAINROLES,
  APIREQUESTS,
} from "@/helpers";
import { Modal, SelectableStarsRating, ConfirmationDialog } from "@/components";
import starIcon from "@/assets/images/star.svg";
import { useGlobalStore } from '@/store/Modules/global';

export default {
  name: "StoreRate",
  setup() {
    const global = useGlobalStore() || {};
    return {
      global
    }
  },
  data() {
    return {
      starIcon,
      isLoading: false,
      isLoadingMore: false,
      isRatingFormSubmitted: false,
      showFormShadows: false,
      rateForm: {
        selectedRating: 0,
        comment: "",
      },
      hasReachedFullPages: false,
      comments: [],
    };
  },
  components: {
    StarRating,
    Star,
    Modal,
    SelectableStarsRating,
    ConfirmationDialog,
  },
  props: {
    isModalOpen: {
      type: Boolean,
      default: false,
      required: true,
    },
    isUpdateDataStore: {
      type: Boolean,
      default: false,
      required: true,
    },
    changeModalState: {
      type: Function,
      required: true,
    },
    refreshStoreData: {
      type: Function,
      required: true,
    },
    ratingData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    editedRating: {
      type: Object,
      default: null,
    },
  },
  methods: {
    arabizeNumbers,
    roundWithHalf,
    addRating() {
      this.isLoading = true;
      this.isRatingFormSubmitted = true;
      const storeId = this.ratingData?.store?.id;
      if (this.rateForm?.selectedRating <= 0) {
        this.isLoading = false;
        return;
      }

      // If in edit mode, use edit endpoint
      if (this.isEditMode) {
        this.makeRequest({
          owner: MAINROLES.user,
          url: `${APIREQUESTS.user.editStoreRating.url}/${this.editedRating.id}`,
          method: APIREQUESTS.user.editStoreRating.method,
          formData: {
            store_id: storeId,
            rating: +this.rateForm.selectedRating,
            comment: this.rateForm.comment,
          },
        })
          .then((res) => {
            if (res?.status) {
              this.isRatingFormSubmitted = false;
              this.notify({
                type: "success",
                msg: res?.data?.message || this.$t("general.rating_updated_successfully"),
              });
              this.refreshStoreData()?.then(() => {
                this.changeModalState(false);
              });
              this.rateForm = {
                selectedRating: 0,
                comment: "",
              };
            } else {
              this.notify({
                type: "error",
                msg: res?.message || this.$t("general.failed"),
              });
            }
          })
          .catch((err) => {
            this.notify({
              type: "error",
              msg: err?.data?.message || this.$t("general.failed"),
            });
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        // Add new rating
        this.makeRequest({
          owner: MAINROLES.user,
          url: APIREQUESTS.user.addStoreRating.url,
          method: APIREQUESTS.user.addStoreRating.method,
          formData: {
            store_id: storeId,
            rating: +this.rateForm.selectedRating,
            comment: this.rateForm.comment,
          },
        })
          .then((res) => {
            if (res?.status) {
              this.isRatingFormSubmitted = false;
              // TODO: modify the store data instead of calling store date APIs
              // update only the value of these following keys:
              // 1- ratings_avg
              // 2- ratings_count
              this.refreshStoreData()?.then(() => {
                this.changeModalState(false);
              });

              const myNewlyAddedComment = res?.data || {};
              this.comments = [myNewlyAddedComment, ...(this.comments || [])];

              this.rateForm = {
                selectedRating: 0,
                comment: "",
              };
            } else {
              this.notify({
                type: "error",
                msg: res?.message || this.$t("general.failed"),
              });
            }
          })
          .catch((err) => {
            this.notify({
              type: "error",
              msg: err?.data?.message || this.$t("general.failed"),
            });
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
  },
  computed: {
    currentUserId() {
      return this.global?.localStorageCopy?.user?.id;
    },
    hasGivenFeedback() {
      return this.comments?.length > 0
        ? this.comments.some((rate) => rate.user_id == this.currentUserId)
        : false;
    },
    isEditMode() {
      return !!this.editedRating && !!this.editedRating.id;
    },
  },
  watch: {
    isModalOpen: {
      immediate: true,
      handler(val) {
        if (typeof val === "boolean") {
          if (val && this.isEditMode && this.editedRating) {
            // Pre-fill form with existing rating data when editing
            this.rateForm = {
              selectedRating: this.editedRating.rating || 0,
              comment: this.editedRating.comment || "",
            };
          } else {
            // reset the rateForm when the modal is closed or opened for new rating
            this.rateForm = {
              selectedRating: 0,
              comment: "",
            };
          }
          this.isRatingFormSubmitted = false;
        }
      },
    },
    editedRating: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.isModalOpen) {
          // Pre-fill form when editedRating changes
          this.rateForm = {
            selectedRating: newVal.rating || 0,
            comment: newVal.comment || "",
          };
        }
      },
    },
  },
};
</script>
